import { evaluateRules } from './evaluator.js';
import { assertGeneratedReportSafety } from './outputSafety.js';
import type { RelationshipContextBundle, RelationshipContextType } from './relationshipContext.js';
import { extractRelationshipFeatures } from './relationshipContext.js';
import { resolveInterpretation } from './resolver.js';
import { assertRulePackIntegrity } from './rulePackValidation.js';
import type { InterpretationRule, InterpretationStrength, TriggeredRule } from './rules.js';
import { selectTemplateVariant, type TemplateDefinition } from './template.js';

export interface RelationshipRulePack {
  id: string;
  version: string;
  compatibleCalculationRulesets: readonly string[];
  compatibleRelationshipTypes: readonly RelationshipContextType[];
  rules: readonly InterpretationRule[];
}

export interface RelationshipTemplatePack {
  id: string;
  version: string;
  templates: Readonly<Record<string, TemplateDefinition>>;
}

export interface RelationshipRuleProvenance {
  ruleId: string;
  sourceId: string;
  sourceTitle: string;
  sourceUrl: string | undefined;
  sourceLocator: string;
  normalizedRationale: string;
}

export interface RelationshipClaim {
  claimKey: string;
  text: string;
  ruleIds: readonly string[];
  sourceIds: readonly string[];
  sourceLocators: readonly string[];
  ruleProvenance: readonly RelationshipRuleProvenance[];
  evidenceRefs: readonly string[];
  evidenceGroups: readonly string[];
  strength: InterpretationStrength;
  culturalFramework: 'traditional_ziwei_doushu';
}

export interface GeneratedRelationshipReport {
  relationshipType: RelationshipContextType;
  mappingLabel: string;
  contextFingerprint: string;
  topics: readonly [{
    topic: 'relationship';
    claims: readonly RelationshipClaim[];
    resolved: ReturnType<typeof resolveInterpretation>;
    versions: {
      personACalculationRuleset: string;
      personBCalculationRuleset: string;
      relationshipRulePack: string;
      interpretationRules: readonly string[];
      templatePack: string;
    };
  }];
  interpretationStatus: 'research_pending' | 'source_backed_rules_available';
  disclosure: string;
}

export const RELATIONSHIP_CONTEXT_RULE_PACK_0_1_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-CONTEXT',
  version: '0.1.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['partner', 'siblings', 'parent_to_child', 'child_to_parent']),
  rules: Object.freeze([]),
});

export const RELATIONSHIP_CONTEXT_TEMPLATE_PACK_0_1_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.1.0',
  templates: Object.freeze({}),
});

function claimKeysForTrace(trace: TriggeredRule): readonly string[] {
  return [...new Set(trace.rule.effects.map((effect) => effect.claimKey))].sort();
}

function assertRelationshipCompatibility(context: RelationshipContextBundle, rulePack: RelationshipRulePack): void {
  if (!rulePack.compatibleRelationshipTypes.includes(context.relationshipType)) {
    throw new Error(`Relationship rule pack ${rulePack.id}@${rulePack.version} is not compatible with relationship type ${context.relationshipType}`);
  }
  for (const person of [context.personA, context.personB] as const) {
    if (!rulePack.compatibleCalculationRulesets.includes(person.calculationRulesetId)) {
      throw new Error(`Relationship rule pack ${rulePack.id}@${rulePack.version} is not compatible with ${person.role} calculation ruleset ${person.calculationRulesetId}`);
    }
  }
}

export function generateRelationshipReport(
  context: RelationshipContextBundle,
  rulePack: RelationshipRulePack = RELATIONSHIP_CONTEXT_RULE_PACK_0_1_0,
  templatePack: RelationshipTemplatePack = RELATIONSHIP_CONTEXT_TEMPLATE_PACK_0_1_0,
): GeneratedRelationshipReport {
  assertRelationshipCompatibility(context, rulePack);
  assertRulePackIntegrity(rulePack, templatePack);

  const features = extractRelationshipFeatures(context);
  const traces = evaluateRules(features, rulePack.rules).filter((trace) => trace.rule.topic === 'relationship');
  const resolved = resolveInterpretation('relationship', traces);
  const claimKeys = [...new Set(traces.flatMap(claimKeysForTrace))].sort();
  const claims: RelationshipClaim[] = claimKeys.map((claimKey) => {
    const matching = traces.filter((trace) => claimKeysForTrace(trace).includes(claimKey));
    const template = templatePack.templates[claimKey];
    if (!template) throw new Error(`Missing relationship template for claim ${claimKey}`);
    return {
      claimKey,
      text: selectTemplateVariant(template, [context.contextFingerprint, context.relationshipType, claimKey, templatePack.id, templatePack.version]),
      ruleIds: [...new Set(matching.map((trace) => trace.rule.id))].sort(),
      sourceIds: [...new Set(matching.map((trace) => trace.rule.source.sourceId))].sort(),
      sourceLocators: [...new Set(matching.map((trace) => trace.rule.sourceLocator).filter((value): value is string => Boolean(value)))].sort(),
      ruleProvenance: matching.map((trace) => ({
        ruleId: trace.rule.id,
        sourceId: trace.rule.source.sourceId,
        sourceTitle: trace.rule.source.title,
        sourceUrl: trace.rule.source.sourceUrl,
        sourceLocator: trace.rule.sourceLocator ?? '',
        normalizedRationale: trace.rule.normalizedRationale ?? '',
      })).sort((a, b) => a.ruleId.localeCompare(b.ruleId)),
      evidenceRefs: [...new Set(matching.flatMap((trace) => trace.evidenceRefs))].sort(),
      evidenceGroups: [...new Set(matching.flatMap((trace) => trace.evidenceGroups))].sort(),
      strength: resolveInterpretation('relationship', matching).strength,
      culturalFramework: 'traditional_ziwei_doushu' as const,
    };
  });

  const report: GeneratedRelationshipReport = {
    relationshipType: context.relationshipType,
    mappingLabel: context.mapping.directionLabel,
    contextFingerprint: context.contextFingerprint,
    topics: [{
      topic: 'relationship',
      claims,
      resolved,
      versions: {
        personACalculationRuleset: `${context.personA.calculationRulesetId}@${context.personA.calculationRulesetVersion}`,
        personBCalculationRuleset: `${context.personB.calculationRulesetId}@${context.personB.calculationRulesetVersion}`,
        relationshipRulePack: `${rulePack.id}@${rulePack.version}`,
        interpretationRules: [...new Set(traces.map((trace) => `${trace.rule.id}@${trace.rule.version}`))].sort(),
        templatePack: `${templatePack.id}@${templatePack.version}`,
      },
    }],
    interpretationStatus: rulePack.rules.length === 0 ? 'research_pending' : 'source_backed_rules_available',
    disclosure: rulePack.rules.length === 0
      ? '目前尚無通過 Source Registry 與測試 Gate 的正式雙人關係判讀規則，因此只顯示客觀關係 Context 與 Evidence，不以 AI、相合分數或無來源文案補足。'
      : '雙人關係解讀只輸出已註冊來源且條件實際觸發的規則；不提供科學相容度、相合百分比或必然關係結果。',
  };
  const knownEvidenceRefs = new Set(features.flatMap((feature) => feature.evidenceRefs));
  assertGeneratedReportSafety(report, knownEvidenceRefs);
  return report;
}
